import {NgTemplateOutlet} from '@angular/common';
import {booleanAttribute, Component, computed, inject, input} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {MatTooltip} from '@angular/material/tooltip';
import {filter, map} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {CheckpointStatusOrbsEnum} from '../../../classes/checkpoint-status-orbs.enum';
import {Routenames} from '../../../classes/route-name.enum';
import {User} from '../../../classes/user.class';
import {AuthorisationService} from '../../../services/auth/authorisation.service';
import {Features} from '../../../services/auth/features';
import {Functionalities} from '../../../services/auth/functionalities';
import {SIDEBAR_ICONS, SidebarIcon} from './sidebar-icons';
import {SidebarStateService} from './sidebar-state.service';

interface NavItem {
    label: string;
    icon: SidebarIcon;
    link: string[];
    /** Routes without an item of their own that belong to this one (e.g. /platforms under Seire). */
    alsoActiveUnder?: string;
    /** Count shown as an orange badge (a dot when collapsed); nothing when 0. */
    badge?: number;
    /** Tooltip when collapsed; defaults to the label. */
    tooltip?: string;
}

interface NavGroup {
    title: string;
    items: NavItem[];
}

/** Worst status per checkpoint level over all platforms. */
export interface SeireStatusOrbs {
    production: CheckpointStatusOrbsEnum;
    health: CheckpointStatusOrbsEnum;
    check: CheckpointStatusOrbsEnum;
    acceptance: CheckpointStatusOrbsEnum;
}

const ORB_LABELS: Record<CheckpointStatusOrbsEnum, string> = {
    [CheckpointStatusOrbsEnum.error]: 'Storing',
    [CheckpointStatusOrbsEnum.warning]: 'Waarschuwing',
    [CheckpointStatusOrbsEnum.alert]: 'Opgeschort',
    [CheckpointStatusOrbsEnum.success]: 'Goed',
    [CheckpointStatusOrbsEnum.disabled]: 'Uitgeschakeld',
    [CheckpointStatusOrbsEnum.inactive]: 'Geen checks',
};

/**
 * Dark sidebar navigation (232px, 56px collapsed). The items keep the rights the top navbar had;
 * collapsed, only the icons remain with the label as tooltip. Under the logo are the four Seire status
 * orbs, like next to the logo in the former navbar: side by side, and below each other when collapsed.
 */
@Component({
    selector: 'app-sidebar',
    imports: [RouterLink, RouterLinkActive, MatTooltip, NgTemplateOutlet],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
    host: {
        '[class.collapsed]': 'state.collapsed()',
        '[style.width.px]': 'state.width()'
    }
})
export class SidebarComponent {
    readonly user = input<User | null>(null);
    readonly loggedIn = input(false, {transform: booleanAttribute});
    /** Leave requests waiting for approval (same source as the former orange dot next to Verlof). */
    readonly unapprovedLeaves = input(0);
    readonly seireStatus = input<SeireStatusOrbs | null>(null);

    readonly state = inject(SidebarStateService);
    readonly icons = SIDEBAR_ICONS;
    readonly Routenames = Routenames;

    private readonly router = inject(Router);
    private readonly url = toSignal(this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => event.urlAfterRedirects)
    ), {initialValue: this.router.url});

    /** The groups with only the items this user may see (same checks as the former navbar). */
    readonly groups = computed<NavGroup[]>(() => {
        const user = this.user();
        const waiting = this.unapprovedLeaves() || 0;
        const can = (functionality: Functionalities) => AuthorisationService.hasFunctionality(functionality, user);
        const groups: { title: string; items: (NavItem | false)[] }[] = [
            {
                title: 'Monitoring',
                items: [
                    can(Functionalities.Seire) && {label: 'Seire', icon: 'seire', link: ['/', Routenames.seire], alsoActiveUnder: Routenames.platforms},
                ],
            },
            {
                title: 'Werk',
                items: [
                    can(Functionalities.Daily) && {label: 'Daily', icon: 'daily', link: ['/', Routenames.daily]},
                    can(Functionalities.Weekly) && {label: 'Weekly', icon: 'weekly', link: ['/', Routenames.weekly]},
                    can(Functionalities.HourRegistration) && {label: 'Urenregistratie', icon: 'registration', link: ['/', Routenames.registration]},
                    can(Functionalities.HourRegistration) && {
                        label: 'Verlof',
                        icon: 'leave',
                        link: ['/', Routenames.leave],
                        badge: waiting,
                        tooltip: waiting > 0 ? `Verlof · ${waiting} wachten op goedkeuring` : 'Verlof'
                    },
                ],
            },
            {
                title: 'Beheer',
                items: [
                    {label: 'Rapportages', icon: 'reports', link: ['/', Routenames.reports]},
                    AuthorisationService.hasFeature(Features.Companies, user) && {label: 'Bedrijven', icon: 'companies', link: ['/', Routenames.companies]},
                    can(Functionalities.Users) && {label: 'Gebruikers', icon: 'users', link: ['/', Routenames.users]},
                    !environment.production && !environment.test && can(Functionalities.Permissions) && {label: 'Rechtenmatrix', icon: 'rights', link: ['/', Routenames.rightsMatrix]},
                    can(Functionalities.Subscriptions) && {label: 'Abonnementen', icon: 'subscriptions', link: ['/', Routenames.subscriptions]},
                    can(Functionalities.Financials) && {label: 'Financieel', icon: 'financials', link: ['/', Routenames.financials]},
                    can(Functionalities.PdfUnderlay) && {label: 'Briefpapier', icon: 'underlay', link: ['/', Routenames.pdf, Routenames.underlay]},
                ],
            },
        ];
        return groups
            .map(group => ({title: group.title, items: group.items.filter((item): item is NavItem => !!item)}))
            .filter(group => group.items.length);
    });

    readonly orbs = computed(() => {
        const status = this.seireStatus();
        if (!status) {
            return [];
        }
        return [
            {label: 'Production', state: status.production},
            {label: 'Health', state: status.health},
            {label: 'Check', state: status.check},
            {label: 'Acceptance', state: status.acceptance},
        ].map(orb => ({...orb, tooltip: `${orb.label}: ${ORB_LABELS[orb.state] ?? ORB_LABELS[CheckpointStatusOrbsEnum.inactive]}`}));
    });

    /** The four statuses in the name of the link, because the tooltips on the orbs can't be reached by keyboard. */
    readonly statusLabel = computed(() => `Status platformen: ${this.orbs().map(orb => orb.tooltip).join(', ')}. Open Seire`);

    isUnder(route: string | undefined): boolean {
        return !!route && this.url().startsWith(`/${route}`);
    }
}
